// src/pages/Register.jsx
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button, Input, Card, CardContent } from '../components/ui';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { saveAttributionToProfile } from '../hooks/useAttribution';

export default function Register() {
  const [loading, setLoading]           = useState(false);
  const [showSuccess, setShowSuccess]   = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams]                  = useSearchParams();
  const [formData, setFormData]         = useState({
    email:         '',
    password:      '',
    termsAccepted: false,
  });

  // ── Capture referral code from URL and persist it ──────────────────────────
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      try { localStorage.setItem('revhapi_ref', ref.toUpperCase().trim()); } catch {}
    }
  }, [searchParams]);

  const refCode = (() => {
    try { return localStorage.getItem('revhapi_ref'); } catch { return null; }
  })();

  // ── Google OAuth ───────────────────────────────────────────────────────────
  const handleGoogleSignUp = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/onboarding`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (error) {
      toast.error(error.message || 'Failed to sign up with Google');
      setLoading(false);
    }
  };

  // ── Email registration ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) { toast.error('Please fill in all fields'); return; }
    if (formData.password.length < 6)           { toast.error('Password must be at least 6 characters'); return; }
    if (!formData.termsAccepted)                { toast.error('Please accept the Terms of Service and Privacy Policy'); return; }

    setLoading(true);
    const tid = toast.loading('Creating your account...');

    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:        formData.email,
          password:     formData.password,
          referralCode: refCode || null,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        try { localStorage.removeItem('revhapi_ref'); } catch {}
        // Save any attribution data (UTMs, affiliate codes) to the new user's profile
        if (data.userId) {
          saveAttributionToProfile(data.userId);
        }
        toast.success('Check your email to verify your account!', { id: tid });
        setShowSuccess(true);
      } else {
        toast.error(data.error || 'Registration failed', { id: tid });
      }
    } catch (error) {
      toast.error('Failed to create account. Please try again.', { id: tid });
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="w-full max-w-md text-center">
          <a href="https://revhapi.com" className="flex items-center justify-center gap-2 mb-8">
            <img src="/logo-icon.svg" alt="RevHapi" className="h-10 w-10" />
            <span className="text-2xl font-bold">RevHapi</span>
          </a>
          <Card>
            <CardContent className="pt-6 pb-8 space-y-6">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Check your email</h2>
                <p className="text-muted-foreground">
                  We've sent a verification link to<br />
                  <strong className="text-foreground">{formData.email}</strong>
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                Click the link in the email to verify your account and get started.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
      <div className="w-full max-w-md space-y-6">
        <a href="https://revhapi.com" className="flex items-center justify-center gap-2">
          <img src="/logo-icon.svg" alt="RevHapi" className="h-10 w-10" />
          <span className="text-2xl font-bold">RevHapi</span>
        </a>

        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="text-center">
              <h1 className="text-2xl font-bold">Create your account</h1>
              <p className="text-muted-foreground text-sm mt-1">Start your 30-day free trial</p>
              {refCode && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                  ✓ Referred by affiliate code <strong>{refCode}</strong>
                </div>
              )}
            </div>

            {/* Google */}
            <Button variant="outline" className="w-full" onClick={handleGoogleSignUp} disabled={loading}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="h-4 w-4 mr-2" />
              Continue with Google
            </Button>

            <div className="relative flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Email form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" placeholder="you@example.com" value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10" disabled={loading} required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Must be at least 6 characters</p>
              </div>

              <div className="flex items-start gap-2">
                <input type="checkbox" id="terms" checked={formData.termsAccepted}
                  onChange={e => setFormData({ ...formData, termsAccepted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-border" disabled={loading} />
                <label htmlFor="terms" className="text-sm text-muted-foreground">
                  I agree to the{' '}
                  <a href="https://revhapi.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms of Service</a>{' '}
                  and{' '}
                  <a href="https://revhapi.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
                </label>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating account…' : 'Create Account'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
